<template>
  <div
    ref="containerRef"
    v-safe-html="processedHtmlContent"
    class="base-rich-text-preview"
    :class="[
      `base-rich-text-preview--${profile}`,
      {
        'base-rich-text-preview--image-previewable': enableImagePreview,
        'tdc-rich-text-content': profile === 'tdc',
      },
    ]"
  ></div>
  <client-only>
    <a-image-preview-group
      v-if="enableImagePreview"
      v-model:visible="imagePreviewVisible"
      v-model:current="imagePreviewCurrent"
      infinite
      :src-list="imagePreviewSrcList"
    />
  </client-only>
</template>

<script setup>
import { getOssUrlWithoutAvifForPreview } from '~/utils'
import { rewriteRichTextOssUrls } from '~/utils/rewriteRichTextOssUrls'
import { initRichTextSwipers } from '~/utils/richTextSwiper'
import { useVideoPlayer } from '~/composables/useVideoPlayer'
import { useSolutionHomeSignedFileLoader } from '@/composables/useSolutionHomeSignedFile'
import { BaseMessage } from '~/utils/baseMessage'
import { isOssSignedUrl, resolveSolutionHomeSignedFileUrl } from '~/utils/ossSignedUrl'

const loadSolutionHomeContent = useSolutionHomeSignedFileLoader()

/**
 * @description 富文本预览组件，用于安全渲染 vue-tinymce 等产出的 HTML
 * @param {string} htmlContent - 已处理的富文本 HTML 字符串
 * @param {boolean} enableImagePreview - 是否支持点击正文图片查看大图
 * @param {'legacy'|'tdc'|'official'} profile - 正文样式配置；official 仅使用随正文保存的官网样式
 * @example
 * <BaseRichTextPreview :html-content="processedHtml" />
 */

const VIDEO_UNPLAYABLE_HINT_TEXT =
  '当前浏览器无法解码或无法播放该视频，请尝试更换其他浏览器打开本页。'

/** IE 等对 AVI 等常不设置 MediaError、duration 为 0，仅靠 error 事件不可靠 */
const WEB_INCOMPATIBLE_VIDEO_PATH = /\.(avi|wmv|flv|mkv|mpeg|mpg|ogv|rm|rmvb)(\?|$)/i

const config = useRuntimeConfig()

const props = defineProps({
  /**
   * @description 要渲染的富文本 HTML（会经 v-safe-html 做安全过滤）
   * @type {string}
   * @default ''
   */
  htmlContent: {
    type: String,
    default: '',
  },
  /**
   * @description 是否支持点击正文图片查看大图（帮助中心/政策等详情页默认开启）
   * @type {boolean}
   * @default true
   */
  enableImagePreview: {
    type: Boolean,
    default: true,
  },
  /**
   * @description 正文样式配置；legacy 保留旧 TinyMCE 样式，tdc 对齐 TDC wangEditor，official 不叠加通用排版
   * @type {'legacy'|'tdc'|'official'}
   * @default 'legacy'
   */
  profile: {
    type: String,
    default: 'legacy',
    validator: (value) => ['legacy', 'tdc', 'official'].includes(value),
  },
})

const containerRef = ref(null)
const { syncVideoBindings } = useVideoPlayer()
let swiperRefreshers = []
let imageClickBound = false
let officialSignedLinkBound = false

const imagePreviewVisible = ref(false)
const imagePreviewCurrent = ref(0)
const imagePreviewSrcList = ref([])

/**
 * @description 解析富文本图片可用于预览的地址
 * @param {HTMLImageElement} image - 图片节点
 * @returns {string}
 */
function resolvePreviewImageUrl(image) {
  if (!image) return ''
  const raw =
    image.currentSrc ||
    image.getAttribute('src') ||
    image.getAttribute('data-src') ||
    image.getAttribute('data-mce-src') ||
    ''
  if (!raw || raw.startsWith('data:')) return ''
  try {
    return getOssUrlWithoutAvifForPreview(new URL(raw, window.location.href).href)
  } catch {
    return getOssUrlWithoutAvifForPreview(raw)
  }
}

/**
 * @description 判断图片是否属于已禁用的旧官网跳转区域
 * @param {HTMLImageElement} image - 图片节点
 * @returns {boolean}
 */
function isDisabledLegacyLinkImage(image) {
  return !!image?.closest?.('.legacy-official-link-disabled')
}

/**
 * @description 收集容器内可预览图片地址列表
 * @returns {string[]}
 */
function collectPreviewImageUrls() {
  if (!containerRef.value) return []
  return Array.from(containerRef.value.querySelectorAll('img'))
    .filter((image) => !isDisabledLegacyLinkImage(image))
    .map((image) => resolvePreviewImageUrl(image))
    .filter(Boolean)
}

/**
 * @description 点击正文图片时打开 Arco 大图预览（支持多图左右切换）
 * @param {MouseEvent} event - 点击事件
 */
function handleRichTextImageClick(event) {
  if (!props.enableImagePreview || !import.meta.client) return

  const image = event.target?.closest?.('img')
  if (!image || !containerRef.value?.contains(image)) return
  if (isDisabledLegacyLinkImage(image)) return

  // 轮播拖拽结束后的合成 click 由 swiper 捕获阶段拦截；此处再兜底忽略无地址图片
  const clickedUrl = resolvePreviewImageUrl(image)
  if (!clickedUrl) return

  event.preventDefault()
  event.stopPropagation()

  const srcList = collectPreviewImageUrls()
  const current = Math.max(0, srcList.indexOf(clickedUrl))
  imagePreviewSrcList.value = srcList.length > 0 ? srcList : [clickedUrl]
  imagePreviewCurrent.value = current
  imagePreviewVisible.value = true
}

/**
 * @description 绑定正文图片点击预览（事件委托，避免每次 HTML 更新重复绑节点）
 */
function bindImagePreview() {
  if (!import.meta.client || !containerRef.value || !props.enableImagePreview) return
  if (imageClickBound) return
  containerRef.value.addEventListener('click', handleRichTextImageClick)
  imageClickBound = true
}

/**
 * @description 解绑正文图片点击预览
 */
function unbindImagePreview() {
  if (!containerRef.value || !imageClickBound) return
  containerRef.value.removeEventListener('click', handleRichTextImageClick)
  imageClickBound = false
}

/**
 * @description 官网方案正文里的签名文件点击时先刷新再打开，避免页面静置后签名过期。
 * @param {MouseEvent} event - 点击事件
 */
async function handleOfficialSignedLinkClick(event) {
  if (props.profile !== 'official' || !import.meta.client) return
  if (event.target?.closest?.('img') && props.enableImagePreview) return

  const anchor = event.target?.closest?.('a')
  if (!anchor || !containerRef.value?.contains(anchor)) return
  if (anchor.classList?.contains('legacy-official-link-disabled')) return

  const href = anchor.getAttribute('href') || ''
  if (!isOssSignedUrl(href)) return

  event.preventDefault()
  event.stopPropagation()
  try {
    const nextUrl = await resolveSolutionHomeSignedFileUrl(href, loadSolutionHomeContent)
    window.open(nextUrl, '_blank', 'noopener,noreferrer')
  } catch (error) {
    BaseMessage.error(error?.message || '方案文件打开失败')
  }
}

function bindOfficialSignedLinkPreview() {
  if (!import.meta.client || !containerRef.value || props.profile !== 'official') return
  if (officialSignedLinkBound) return
  containerRef.value.addEventListener('click', handleOfficialSignedLinkClick)
  officialSignedLinkBound = true
}

function unbindOfficialSignedLinkPreview() {
  if (!containerRef.value || !officialSignedLinkBound) return
  containerRef.value.removeEventListener('click', handleOfficialSignedLinkClick)
  officialSignedLinkBound = false
}

/**
 * 富文本内 video 预处理：播放控件、内联播放、X5、referrerpolicy（减轻跨站跟踪相关控制台提示）
 */
function processVideoForMobile(html) {
  if (!html || typeof html !== 'string') return html
  return html.replace(/<video(\s[^>]*)?>/gi, (match) => {
    let rest = match.slice(6)
    const hasControls = /\bcontrols\b/i.test(rest)
    const hasPlaysinline = /playsinline/i.test(rest)
    const hasWebkitPlaysinline = /webkit-playsinline/i.test(rest)
    const hasPreload = /preload\s*=/i.test(rest)
    const hasX5Playsinline = /x5-playsinline/i.test(rest)
    const hasX5PlayerType = /x5-video-player-type/i.test(rest)
    const hasReferrerPolicy = /referrerpolicy\s*=/i.test(rest)
    const hasControlsList = /controlslist\s*=/i.test(rest)
    const hasAutoplay = /\bautoplay\b/i.test(rest)
    const hasMuted = /\bmuted\b/i.test(rest)
    let extra = ''
    if (!hasControls) extra += ' controls'
    if (!hasPlaysinline) extra += ' playsinline'
    if (!hasWebkitPlaysinline) extra += ' webkit-playsinline'
    if (!hasPreload) extra += ' preload="metadata"'
    if (!hasX5Playsinline) extra += ' x5-playsinline'
    if (!hasX5PlayerType) extra += ' x5-video-player-type="h5"'
    if (!hasReferrerPolicy) extra += ' referrerpolicy="strict-origin-when-cross-origin"'
    if (!hasControlsList) {
      extra += ' controlslist="nodownload"'
    } else {
      rest = rest.replace(/controlslist\s*=\s*(["'])(.*?)\1/i, (attribute, quote, value) => {
        const controls = String(value).split(/\s+/).filter(Boolean)
        if (!controls.includes('nodownload')) controls.push('nodownload')
        return `controlslist=${quote}${controls.join(' ')}${quote}`
      })
    }
    if (hasAutoplay && !hasMuted) extra += ' muted'
    return '<video' + extra + (rest || ' ') + '>'
  })
}

function processAudioControls(html) {
  if (!html || typeof html !== 'string') return html
  return html.replace(/<audio(\s[^>]*)?>/gi, (match) => {
    const rest = match.slice(6)
    const hasControls = /\bcontrols\b/i.test(rest)
    const hasReferrerPolicy = /referrerpolicy\s*=/i.test(rest)
    let extra = ''
    if (!hasControls) extra += ' controls'
    if (!hasReferrerPolicy) extra += ' referrerpolicy="strict-origin-when-cross-origin"'
    return '<audio' + extra + (rest || ' ') + '>'
  })
}

function processRichTextMedia(html) {
  const preferred = config.public.productImageCdnOrigin || config.public.staticAssetOrigin || ''
  let out = rewriteRichTextOssUrls(html, preferred)
  out = processAudioControls(processVideoForMobile(out))
  return out
}

const processedHtmlContent = computed(() => processRichTextMedia(props.htmlContent))

/**
 * @description 解析 video 可下载地址（含 source 子节点）
 * @param {HTMLVideoElement} video - 视频节点
 * @returns {string}
 */
function resolveVideoFileUrl(video) {
  try {
    const raw = video.currentSrc || video.src
    if (raw) return new URL(raw, window.location.href).href
    const first = video.querySelector('source[src]')
    const s = first && first.getAttribute('src')
    if (s) return new URL(s, window.location.href).href
  } catch {
    /* ignore */
  }
  return ''
}

/**
 * @description 插入视频无法播放提示区，不提供视频下载入口。
 * @param {HTMLVideoElement} video - 视频节点
 * @param {string} text - 提示文案
 * @returns {void}
 */
function insertVideoFallback(video, text) {
  if (video.dataset.fallbackInserted) return
  video.dataset.fallbackInserted = '1'
  const wrap = document.createElement('div')
  wrap.className = 'base-rich-text-preview__video-fallback'
  wrap.setAttribute('role', 'status')

  const textEl = document.createElement('p')
  textEl.className = 'base-rich-text-preview__video-fallback__text'
  textEl.textContent = text
  wrap.appendChild(textEl)

  if (video.parentNode) {
    video.parentNode.insertBefore(wrap, video.nextSibling)
  }
}

/**
 * @description 根据地址判断常见无法在网页 video 中直接解码的容器后缀
 * @param {string} url - 绝对或相对地址
 * @returns {boolean}
 */
function isLikelyWebIncompatibleContainerUrl(url) {
  if (!url || typeof url !== 'string') return false
  try {
    const path = new URL(url, window.location.href).pathname
    return WEB_INCOMPATIBLE_VIDEO_PATH.test(path)
  } catch {
    return false
  }
}

/**
 * @description 从 URL 猜测 MIME，供 canPlayType 使用（IE 等对 AVI 常返回空串）
 * @param {string} url - 绝对地址
 * @returns {string}
 */
function guessVideoMimeFromUrl(url) {
  try {
    const seg = new URL(url, window.location.href).pathname.split('/').pop() || ''
    const base = seg.split('?')[0].toLowerCase()
    if (base.endsWith('.avi')) return 'video/x-msvideo'
    if (base.endsWith('.wmv')) return 'video/x-ms-wmv'
    if (base.endsWith('.flv')) return 'video/x-flv'
    if (base.endsWith('.mkv')) return 'video/x-matroska'
    if (base.endsWith('.mpeg') || base.endsWith('.mpg')) return 'video/mpeg'
    if (base.endsWith('.ogv')) return 'video/ogg'
    return ''
  } catch {
    return ''
  }
}

/**
 * @description 视频节点是否仍在文档中（兼容 IE，不使用 isConnected）
 * @param {HTMLVideoElement} video - 视频节点
 * @returns {boolean}
 */
function isVideoInDocument(video) {
  try {
    return document.documentElement.contains(video)
  } catch {
    return false
  }
}

/**
 * @description 浏览器是否声明无法播放该 MIME
 * @param {HTMLVideoElement} video - 视频节点
 * @param {string} url - 资源地址
 * @returns {boolean}
 */
function browserSaysCannotPlayMime(video, url) {
  const mime = guessVideoMimeFromUrl(url)
  if (!mime) return false
  try {
    return video.canPlayType(mime) === ''
  } catch {
    return false
  }
}

/**
 * @description 若存在 MediaError（浏览器无法加载/解码资源），在视频下方显示提示
 */
function reportHintIfMediaError(video) {
  if (video.dataset.fallbackInserted) return
  if (video.error) {
    insertVideoFallback(video, VIDEO_UNPLAYABLE_HINT_TEXT)
  }
}

/**
 * @description IE 等：无 MediaError、duration 为 0，但地址或 canPlayType 表明无法播放时补提示
 */
function hintIfUnplayableNoMediaErrorIe(video) {
  if (video.dataset.fallbackInserted) return
  if (!isVideoInDocument(video)) return
  if (video.videoWidth > 0 || video.videoHeight > 0) return

  const url = resolveVideoFileUrl(video)
  const badExt = isLikelyWebIncompatibleContainerUrl(url)
  const badMime = browserSaysCannotPlayMime(video, url)

  if (video.error || badExt || badMime) {
    insertVideoFallback(video, VIDEO_UNPLAYABLE_HINT_TEXT)
  }
}

/**
 * @description 播放后仍无有效画面（编码不受支持但未必触发 error），使用同一提示
 */
function tryHintIfDecodedButNoFrame(video) {
  if (video.dataset.fallbackInserted || video.error) return
  if (video.videoWidth > 0 || video.videoHeight > 0) return
  const d = video.duration
  if (!d || !isFinite(d) || d <= 0) return
  if (video.currentTime < 0.8) return
  insertVideoFallback(video, VIDEO_UNPLAYABLE_HINT_TEXT)
}

/**
 * @description 绑定 video：MediaError / canPlayType / 常见不兼容后缀 / 延迟与播放兜底（覆盖 IE）
 */
function attachVideoUnplayableHints(video) {
  if (video.dataset.unplayableHintBound) return
  video.dataset.unplayableHintBound = '1'
  try {
    video.controlsList?.add('nodownload')
  } catch {
    video.setAttribute('controlslist', 'nodownload')
  }
  video.addEventListener('contextmenu', (event) => event.preventDefault())

  const onMediaLifecycle = () => {
    reportHintIfMediaError(video)
  }

  const scheduleIeHint = (delayMs) => {
    window.setTimeout(() => hintIfUnplayableNoMediaErrorIe(video), delayMs)
  }

  video.addEventListener('error', onMediaLifecycle)
  const prevOnError = video.onerror
  video.onerror = function (e) {
    if (typeof prevOnError === 'function') prevOnError.call(video, e)
    onMediaLifecycle()
  }
  ;['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough'].forEach((evt) => {
    video.addEventListener(evt, onMediaLifecycle)
  })

  video.addEventListener('loadedmetadata', () => {
    scheduleIeHint(400)
  })

  scheduleIeHint(900)

  video.addEventListener('play', () => {
    reportHintIfMediaError(video)
    scheduleIeHint(500)
    window.setTimeout(() => hintIfUnplayableNoMediaErrorIe(video), 2800)

    if (video.dataset.decodedFrameProbeBound) return
    video.dataset.decodedFrameProbeBound = '1'
    video.addEventListener('timeupdate', () => tryHintIfDecodedButNoFrame(video))
    video.addEventListener(
      'playing',
      () => {
        window.setTimeout(() => tryHintIfDecodedButNoFrame(video), 2200)
      },
      { once: true }
    )
  })

  onMediaLifecycle()
  if (video.readyState >= 1) {
    onMediaLifecycle()
  }
}

/** 为富文本内的 video 绑定无法播放提示 */
function bindVideoErrorFallback() {
  if (!import.meta.client || !containerRef.value) return
  const videos = Array.from(containerRef.value.querySelectorAll('video'))
  syncVideoBindings(videos)
  videos.forEach((video) => {
    try {
      video.playsInline = true
    } catch {
      /* ignore */
    }
    attachVideoUnplayableHints(video)
  })
}

/**
 * @description 给富文本挂载后置增强：video 兼容提示、旧 Swiper 轮播还原、图片点击预览。
 */
function enhanceRichTextContent() {
  bindVideoErrorFallback()
  bindImagePreview()
  bindOfficialSignedLinkPreview()
  if (!import.meta.client || !containerRef.value) return
  const nextRefreshers = initRichTextSwipers(containerRef.value)
  if (nextRefreshers.length > 0) {
    swiperRefreshers = nextRefreshers
  }
}

/** 视口变化时重新计算富文本轮播 slide 宽度和偏移 */
function refreshRichTextSwipers() {
  swiperRefreshers.forEach((refresh) => refresh())
}

onMounted(() => {
  nextTick(enhanceRichTextContent)
  if (import.meta.client) {
    window.addEventListener('resize', refreshRichTextSwipers)
  }
})
onUpdated(() => nextTick(enhanceRichTextContent))
onUnmounted(() => {
  unbindImagePreview()
  unbindOfficialSignedLinkPreview()
  if (import.meta.client) {
    window.removeEventListener('resize', refreshRichTextSwipers)
  }
})
</script>

<!-- legacy 保留旧 Tiny 分栏预览；tdc 使用版本化正文契约。 -->
<style lang="scss">
@use 'sass:meta';

@import '~/layouts/styles/rich-text-preview.scss';
@import '~/layouts/styles/tdc-rich-text-preview.scss';
@import '~/layouts/styles/tdc-rich-text-content.scss';

/* OSS 保存的官网正文依赖原站 body 与 reset 的继承值，不叠加 TinyMCE/TDC 排版。 */
.base-rich-text-preview--official {
  color: rgba($color-black, 0.85);
  font-family:
    -apple-system, 'PingFang SC', 'Microsoft Yahei', system-ui, BlinkMacSystemFont, 'Noto Sans SC',
    'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 16px;
  line-height: 1.5;
}

.base-rich-text-preview--official :where(h1, h2, h3, h4, h5, h6, p) {
  margin: 0;
}

.base-rich-text-preview--official :where(h1, h2, h3, h4, h5, h6) {
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
}

.base-rich-text-preview--official :where(img) {
  display: inline;
  max-width: 100%;
  vertical-align: baseline;
}

.base-rich-text-preview--official a.legacy-official-link-disabled,
.base-rich-text-preview--official a.legacy-official-link-disabled:hover,
.base-rich-text-preview--official a.legacy-official-link-disabled:focus,
.base-rich-text-preview--official a.legacy-official-link-disabled:active {
  color: inherit !important;
  text-decoration: none !important;
  cursor: default !important;
}

.base-rich-text-preview--official a.legacy-official-link-disabled :where(*) {
  cursor: default !important;
}
</style>

<style lang="scss" scoped>
.base-rich-text-preview {
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
}

.base-rich-text-preview--tdc {
  overflow-x: auto;
}

.base-rich-text-preview--image-previewable :deep(img) {
  cursor: zoom-in;
}

.base-rich-text-preview :deep(.base-rich-text-preview__video-fallback) {
  margin: 8px 0;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 1.5;
  color: $text-subtitle-color;
  background-color: $bg-color;
  border-radius: 8px;
  border-left: 4px solid $primary-color;

  @include respond-to-max('md') {
    margin: 6px 0;
    padding: 10px 12px;
    font-size: 13px;
  }
}

.base-rich-text-preview :deep(.base-rich-text-preview__video-fallback__text) {
  margin: 0 0 $spacing-xs 0;
  color: $text-content-color;

  &:last-child {
    margin-bottom: 0;
  }
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper-host .swiper-button-disabled) {
  opacity: 0.35;
  pointer-events: none;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper) {
  position: relative;
  overflow: hidden;
  touch-action: pan-y;
  cursor: grab;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper.tenda-rich-text-swiper--dragging) {
  cursor: grabbing;
  user-select: none;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper img) {
  user-select: none;
  -webkit-user-drag: none;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper .swiper-wrapper) {
  display: flex;
  align-items: stretch;
  will-change: transform;
  transition: transform 360ms ease;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper .swiper-slide) {
  box-sizing: border-box;
  flex-shrink: 0;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper .swiper-pagination) {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper .swiper-pagination-bullet) {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.35;
  cursor: pointer;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper .swiper-pagination-bullet-active) {
  opacity: 1;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper-host .swiper-button-prev::after) {
  content: '‹';
  font-size: 18px;
  line-height: 1;
}

.base-rich-text-preview :deep(.tenda-rich-text-swiper-host .swiper-button-next::after) {
  content: '›';
  font-size: 18px;
  line-height: 1;
}
</style>
